"use client";
import React, { FC } from "react";
import { Box, Text } from "@dxp-feed/ui";
import {
  useAppDispatch,
  useAppSelector,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import ImageWrapper from "@/components/ImageWrapper";
import Link from "@/components/Link";
import AnimatedQuantityButton from "@/components/QuantityButton";
import {
  GetUniqueOriginalProductImages,
  useDelayedCallback,
} from "@/helpers/useCommonFunctions";
import { Product } from "@/utils/types/product";
import {
  addDataLayerProductToList,
  dataLayerRemoveFormList,
} from "@/helpers/dataLayer";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { addItemToCartList } from "@/utils/redux/cartProduct/actions";
import {
  setItemListData,
  updateShoppingList,
} from "@/utils/redux/shoppingList";
import { setCartItemListData } from "@/utils/redux/cartProduct";
import { usePathname, useRouter } from "next/navigation";
import { promotionMapping } from "@/utils/promotions";
import PromotionsHeader from "@/components/PromotionsHeader";
import { useGigya } from "@dxp-feed/gigya";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { useStoreDetails } from "@/utils/hooks/useStoreDetails";
import SplitAmount from "@/utils/offers/SplitAmount";
import { showPromotions } from "@/helpers/shoppingListHelper";
import { PromotionKeys, TPromotionKeys } from "@/utils/constant";

export interface AddedProducts {
  item: any;
  Uid: string | null;
  userLoggedIn: boolean | null;
  onResponseProductChange: (itemNumber: number) => void;
  setIsPanelOpen?: (isOpen: boolean) => void;
}

export const CartAddedProducts: FC<AddedProducts> = ({
  item,
  Uid,
  userLoggedIn,
  onResponseProductChange,
  setIsPanelOpen,
}) => {
  const dispatch = useAppDispatch();
  const pathName = usePathname();
  const router = useRouter();
  const cartList: Record<string, any> = useAppSelector(
    (state) => state?.cartProductSlice
  );
  const shoppingList: Record<string, any> = useAppSelector(
    (state) => state?.shoppingListSlice
  );

  const searchDropdown =
  useAppSelector((state) => state.aemStaticSearchData);
  const [incrementDisable, setincrementDisable] = useState(false);
  const [decrementDisable, setdecrementDisable] = useState(false);
  const [articleId, setArticleId] = useState("");
  const [productQty, setProductQty] = useState(0);
  const [isProductQtyClick, setIsProductQtyClick] = useState(false);
  const [loader, setLoader] = useState(false);
  const { gigya } = useGigya();
  const [clickCount, setClickCount] = useState(0);
  const timerRef = useRef<NodeJS.Timeout>(null);

  const gigyaToken = useGigyaAccountInfo();
  const store = localStorage.getItem("store");
  const storeID =  store && JSON.parse(store)?.storeNumber || process.env.NEXT_PUBLIC_DEFAULT_STORE_NUMBER

  const addToCartAnalytics = (item: Product, quantity: number) => {
    const promotionValues = item?.promotions && Object.values(item.promotions);
    const hasPromotionReward = promotionValues?.[0]?.promotionReward;
    const includesY =
      hasPromotionReward?.[0]?.membershipLevelText?.includes("Y");

    let offerTag = "";
    if (includesY) {
      offerTag = "Mass offer - Scene+";
    } else if (hasPromotionReward) {
      offerTag = "Mass offer - Non Scene+";
    }

    addDataLayerProductToList({
      event: `${item?.allergens?.alcohol ? "add_liquor_product_to_list" : "add_products_to_list"}`,
      product_name: item.name,
      item_list_name: cartList?.listName,
      list_name: item?.hierarchicalCategories?.lvl0?.[0] ?? "",
      page_title: document.title,
      offer_tag: offerTag,
    });
    sendSalesforceInteraction({
      name: "cartItem",
      banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
      cartItemStatus: "Add Item to List",
      currency: "$",
      price: item?.price ?? 0,
      quantity: quantity,
      catalogObjectId: `${item?.articleNumber}`,
      catalogObjectType: item.name ?? "",
      isAnonymous: Uid ? 0 : 1,
      gigyaUid: Uid,
      cartEventId: "",
      screenName: document.title, //window.location.href,
      screenClass: window.location.href, //document.title,
      utmSource: getUTMParameter("utm_source"),
      utmMedium: getUTMParameter("utm_medium"),
      utmCampaign: getUTMParameter("utm_campaign"),
    });
  };

  const removeFromCartAnalytics = (item: Product) => {
    const promotionValues = item?.promotions && Object.values(item.promotions);
    const hasPromotionReward = promotionValues?.[0]?.promotionReward;
    const includesY =
      hasPromotionReward?.[0]?.MembershipLevelText?.includes("Y");

    let offerTag = "";
    if (includesY) {
      offerTag = "Mass offer - Scene+";
    } else if (hasPromotionReward) {
      offerTag = "Mass offer - Non Scene+";
    }
    dataLayerRemoveFormList({
      event: "remove_product_from_list",
      product_name: item.name,
      item_list_name: cartList?.listName,
      page_title: document.title,
      offer_tag: offerTag,
    });
    sendSalesforceInteraction({
      name: "cartItem",
      banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
      cartItemStatus: "Remove Item from List",
      currency: "$",
      price: item.price ?? 0,
      quantity: 0,
      catalogObjectId: `${item?.articleNumber}`,
      catalogObjectType: item.name ?? "",
      isAnonymous: Uid ? 0 : 1,
      gigyaUid: Uid,
      cartEventId: "",
      screenName: document.title, //window.location.href,
      screenClass: window.location.href, //document.title,
      utmSource: getUTMParameter("utm_source"),
      utmMedium: getUTMParameter("utm_medium"),
      utmCampaign: getUTMParameter("utm_campaign"),
    });
  };

  const addToCart = useCallback(
    async (product_quantity: number) => {
      const oldquantity =
        cartList?.cartProducts?.products?.find(
          (product: any) => product?.item_id == item.objectID
        )?.quantity ?? 0;
      const updatedQuantity = product_quantity === -1 ? 0 : product_quantity;
      const isShoppedCurrentValue =
        cartList?.cartProducts?.products?.find(
          (product: any) => product?.item_id == item?.objectID
        )?.is_shopped ?? 0;

      try {
        if (updatedQuantity == 0) {
          onResponseProductChange(item?.objectID);
          setLoader(true);
        }
        const data = {
          item_id: `${item?.objectID}`,
          listType: "Product",
          quantity: updatedQuantity,
          is_favourite: 0,
          is_shopped: isShoppedCurrentValue,
          userId: userLoggedIn ? Uid : "",
          storeId: storeID,
        };
        const dataAction = {
          itemId: `${item?.objectID}`,
          listType: "Product",
          quantity: updatedQuantity,
          isFavourite: 0,
          isShopped: isShoppedCurrentValue,
          userToken: "",
          storeId: storeID,
          createdAt: new Date(),
        };
        if (userLoggedIn) {
          const userResponse = await gigyaToken();
          if (userResponse.idToken) {
            dataAction.userToken = userResponse.idToken;

            const result = await dispatch(addItemToCartList(dataAction));
            if (cartList?.listId == shoppingList?.listId) {
              dispatch(updateShoppingList(dataAction));
            }
            if (result && result.type == "add_shopping_list_cart/fulfilled") {
              if (updatedQuantity == 0) {
                onResponseProductChange(0);
              }
            }
            if (result && result.type == "add_shopping_list_cart/rejected") {
              if (updatedQuantity == 0) {
                onResponseProductChange(0);
              }
            }
          }
        } else {
          dispatch(setItemListData(data ?? []));
          dispatch(setCartItemListData(data ?? []));
          if (updatedQuantity == 0) {
            onResponseProductChange(0);
          }
        }
        if (updatedQuantity === 0 || oldquantity > updatedQuantity) {
          removeFromCartAnalytics(item);
        } else {
          addToCartAnalytics(item, updatedQuantity);
        }
      } catch (err: any) {
        console.log("error", err);
      } finally {
        const count =
          cartList?.cartProducts?.products?.reduce(
            (acc: number, product: any) => acc + (product?.quantity || 0),
            0
          ) || 0;
        setIsProductQtyClick(false);
        // setProductQty(0);
        setdecrementDisable(false);
        setincrementDisable(false);
        if (
          (cartList?.cartProducts?.products.length == 1 ||
            cartList?.cartProducts?.products.length == 0) &&
          count == 1 &&
          pathName == "/manage-list"
        ) {
          setTimeout(() => {
            if (typeof window !== "undefined") {
              window.location.reload();
            }
          }, 500);
        }
        setArticleId("");
      }
    },
    [item, userLoggedIn, cartList?.cartProducts?.products]
  );

  const debouncedQuery = useDelayedCallback(
    productQty,
    productQty == 0 || productQty == -1 ? 100 : 800
  );

  useEffect(() => {
    if (isProductQtyClick) {
      addToCart(debouncedQuery);
    }
  }, [debouncedQuery]);

  const [promotionsItemsData, setPromotionsItemsData] = useState<any>([]);

  const fetchPromotionData = async () => {
    try {
      if (searchDropdown?.dropdown?.hidePromotions) return;
      if (showPromotions(searchDropdown?.dropdown?.hideallpromotions ?? [], storeID)) return;
      
      const data = await promotionMapping([item]);
      const key = data?.[0]?.offerType ? PromotionKeys[data[0].offerType as TPromotionKeys] : undefined;
      const dropdownValue = key && searchDropdown?.dropdown ? searchDropdown.dropdown[key as keyof typeof searchDropdown.dropdown] : undefined;
      if (data != undefined) {
        if (item?.hidePromotions || showPromotions(dropdownValue as string[] ?? [], storeID)) setPromotionsItemsData([]);
        else {
          setPromotionsItemsData(data);
        }
      } else {
        setPromotionsItemsData([]);
      }
      return data;

    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchPromotionData();
  }, [item]);
  // Function to process and truncate the promotionFullDesc array
    const truncateProductDescription = (
      data: any[],
      maxLength = 50,
      startLength = 10,
      endLength = 25
    ) => {
      if (!Array.isArray(data)) return data;
      return data.map((item, index) => {
        if (index === 2 && typeof item === 'string' && item.length > maxLength) {
          const start = item.slice(0, startLength);
          const end = item.slice(-endLength);
          return ` ${start} (...) ${end} `;
        }
        return item;
      });
    };
  return (
    <Box as="div" className="border-b-[2px] border-neutral200 px-6">
      <Box as="div" className="flex items-start justify-between py-3">
        <Box as="div" className="flex items-start w-2/3 pr-1">
          <Box as="div" className="min-w-[60px] max-2xs:min-w-[50px] cursor-pointer"
            onClick={() => {
              router.push(`/products/${item?.pageSlug}`);
              setTimeout(() => {
                setIsPanelOpen?.(false);
              }, 1000);
              
            }}>
            <ImageWrapper
              src={`${GetUniqueOriginalProductImages(item?.images)?.[0] ?? "/grocerry.svg"}`}
              alt="Banana Image"
              width={60}
              height={40}
            />
          </Box>

          <Box
            as="div"
            className="pl-3 max-2xs:pl-2 w-full"
          >
            <PromotionsHeader
              productData={promotionsItemsData}
              CardArticalId={item?.articleNumber}
              cardClassName=""
              expClassName="hidden"
            />
            <Text as="div" className="text-body max-2xs:text-[12px]">
              <Text
                as="p"
                onClick={() => {
                  router.push(`/products/${encodeURIComponent(item.pageSlug?.replace(/\./g, '%2E'))}`);
                  setTimeout(() => {
                    setIsPanelOpen?.(false);
                  }, 1000);
                }}
                className="inset-0 z-[1] tab cursor-pointer"
                tabIndex={0}
                aria-label={`Click here to go to ${item?.name} product detail page`}
              >
                
                {promotionsItemsData?.[0]?.offerIdentifier !==
                      "No Min Condition" &&
                      promotionsItemsData?.[0]?.offerIdentifier !==
                      "No Min Condition with Multi Promo" &&
                      promotionsItemsData?.[0]?.offerIdentifier !==
                      "Multi Reward" &&
                      promotionsItemsData?.[0]?.offerIdentifier !==
                      "multi promo type 1" &&
                      promotionsItemsData?.[0]?.offerIdentifier !==
                      "Multi promo only VKAO"  &&
                      promotionsItemsData?.[0]?.offerIdentifier !==
                      "3 Promotions" &&                   
                      promotionsItemsData?.[0]?.promotionDescription && (
                        <Box className="flex justify-start items-start">
                           {(promotionsItemsData?.[0]?.memberType?.includes("Y") || (promotionsItemsData?.[0]?.offerIdentifier ==
                        "multi promo type 2" && promotionsItemsData?.[0]?.memberType?.includes("N") )) ? (
                            <ImageWrapper
                              src="/home/scene-plus.svg"
                              width={16}
                              height={16}
                              className="mr-2 mt-[4px]"
                              alt="scene plus image"
                            />
                          ) : (
                            promotionsItemsData?.[0]?.promotionDescription && (
                              <span className="w-2 h-2 rounded-full bg-red-500 min-w-2 min-h-2 mr-2 mt-[6px]"></span>
                            )
                          )}

                      <Text as="span" className="max-2xs:text-[12px]">
                        {truncateProductDescription(promotionsItemsData?.[0]?.promotionFullDesc, 60, 19, 9)}
                      </Text>
                    </Box>
                  ) || item?.name}

              </Text>
            </Text>
          </Box>
        </Box>

        {/* <Box as="div" className="w-1/4 flex justify-end pl-1">
          {item?.salePrice && item?.salePrice !== item?.price && (
            <Text
              as="p"
              className="font-bold text-red200 max-2xs:text-sm text-base mr-1"
            >
              $
              {(
                item?.salePrice *
                (cartList?.cartProducts?.products?.find(
                  (product: any) => product?.item_id == item.objectID
                )?.quantity || 1)
              ).toFixed(2)}
            </Text>
          )}

          <Text
            as="p"
            className={`font-bold max-2xs:text-sm text-base ${
              item?.salePrice && item?.salePrice !== item?.price
                ? "line-through"
                : "text-brandBlack"
            }`}
          >
            $
            {(
              item?.price *
              (cartList?.cartProducts?.products?.find(
                (product: any) => product?.item_id == item.objectID
              )?.quantity || 1)
            ).toFixed(2)}
          </Text>
        </Box> */}
        <Box as="div" className="w-1/3 flex justify-end">
        <Text as="p" className="flex gap-1 items-center max-2xs:text-sm text-base flex-wrap justify-end">
            {promotionsItemsData &&
            promotionsItemsData?.length > 0 ? (
              <>
                <Box
                  as="div"
                  className="flex gap-1 items-center justify-end flex-wrap"
                >
                  {(promotionsItemsData?.[0]?.offerIdentifier ===
                        "No Min Condition" ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "No Min Condition with Multi Promo" ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "Multi Reward"||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "multi promo type 1" ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "Multi promo only VKAO"  ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "3 Promotions" || 
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "multi promo type 2") && 
                  promotionsItemsData?.[0]?.salePrice <
                    promotionsItemsData?.[0]?.price && (
                      <Text as="span" className="text-red200 font-bold">
                      ${(promotionsItemsData?.[0]?.salePrice *
                          (cartList?.cartProducts?.products?.find(
                          (product: any) => product?.item_id == item.objectID
                        )?.quantity || 1)
                      )?.toFixed(2)}{item?.uom === "KG"
                        ? "/kg"
                        : ""}
                    </Text>
                  )}
                <Text
                  as="span"
                  className={`font-bold max-2xs:text-sm text-base ${(promotionsItemsData?.[0]?.offerIdentifier ===
                    "No Min Condition" ||
                    promotionsItemsData?.[0]?.offerIdentifier ===
                    "No Min Condition with Multi Promo" ||
                    promotionsItemsData?.[0]?.offerIdentifier ===
                    "Multi Reward" ||
                    promotionsItemsData?.[0]?.offerIdentifier ===
                    "multi promo type 1"  ||
                    promotionsItemsData?.[0]?.offerIdentifier ===
                    "Multi promo only VKAO" ||
                    promotionsItemsData?.[0]?.offerIdentifier ===
                    "3 Promotions" || 
                    promotionsItemsData?.[0]?.offerIdentifier ===
                    "multi promo type 2") &&
                    promotionsItemsData?.[0]?.salePrice <
                      promotionsItemsData?.[0]?.price
                      ? "line-through"
                      : ""
                  }`}
                >
                   ${(promotionsItemsData?.[0]?.price *
                    (cartList?.cartProducts?.products?.find(
                      (product: any) => product?.item_id == item.objectID
                    )?.quantity || 1))?.toFixed(2)}{item?.uom === "KG"
                      ? "/kg"
                      : ""}
                </Text>
                </Box>
                {(promotionsItemsData?.[0]?.offerIdentifier ===
                        "No Min Condition" ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "No Min Condition with Multi Promo" ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "Multi Reward"||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "multi promo type 1" ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "Multi promo only VKAO"  ||
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "3 Promotions" || 
                        promotionsItemsData?.[0]?.offerIdentifier ===
                        "multi promo type 2") && 
                  (
                  <>
                    {promotionsItemsData?.[0]?.rewardType == 1 &&
                      promotionsItemsData?.[0]?.salePrice <
                        promotionsItemsData?.[0]?.price && (
                        <Box as="div" className="flex gap-1 items-center">
                          <Text
                            as="span"
                            className="bg-yellow150 text-brandBlack inline-flex xs:text-body text-sm px-[5px] rounded-tl-[5px] rounded-br-[5px] h-[24px] max-h-[24px] items-center font-bold"
                          >
                            SAVE
                          </Text>
                          <Text
                            as="span"
                            className="inline-flex font-bold text-base"
                          >
                            {promotionsItemsData?.[0]
                              ?.rewardValue && (
                              <SplitAmount
                                amount={(
                                  promotionsItemsData?.[0]?.rewardValue?.split(
                                    "."
                                  )[0] / 100
                                )?.toString()}
                              />
                            )}
                          </Text>
                        </Box>
                      )}
                    {promotionsItemsData?.[0]?.rewardType == 2 &&
                      promotionsItemsData?.[0]?.salePrice <
                        promotionsItemsData?.[0]?.price && (
                        <Box as="div" className="flex gap-1 items-center">
                          <Text
                            as="span"
                            className="bg-yellow150 text-brandBlack inline-flex xs:text-body text-sm px-[5px] rounded-tl-[5px] rounded-br-[5px] h-[24px] max-h-[24px] items-center font-bold"
                          >
                            {promotionsItemsData?.items?.[0]?.saveText|| "SAVE"}
                          </Text>
                          <Text
                            as="span"
                            className="inline-flex font-bold xs:text-body text-sm h-[20px]"
                          >
                            {promotionsItemsData?.[0]
                              ?.rewardValue && (
                              <Text
                                as="span"
                                className="pt-[0px] mb-[-6px] inline-flex font-bold max-2xs:text-[12px] text-body h-[20px]"
                              >
                                {
                                  promotionsItemsData?.[0]?.rewardValue
                                    ?.toString()
                                    ?.split(".")[0]
                                }
                                %
                              </Text>
                            )}
                          </Text>
                        </Box>
                      )}
                    {/* {promotionsItemsData?.[0]?.rewardType == 3 &&
                      promotionsItemsData?.[0]?.salePrice <
                        promotionsItemsData?.[0]?.price && (
                        <Box as="div" className="flex gap-1 items-center">
                          <Text
                            as="span"
                            className="bg-yellow150 text-brandBlack inline-flex xs:text-body text-sm px-[5px] rounded-tl-[5px] rounded-br-[5px] h-[24px] max-h-[24px] items-center font-bold"
                          >
                           {promotionsItemsData?.items?.[0]?.saveText || "SAVE"}
                          </Text>
                          <Text
                            as="span"
                            className="inline-flex font-extrabold xs:text-body text-xl"
                          >
                            $
                            {(
                              (promotionsItemsData?.[0]?.price -
                              promotionsItemsData?.[0]?.salePrice)
                            ).toFixed(2)}
                          </Text>
                        </Box>
                      )} */}
                  </>
                )}
              </>
            ) : (
              item?.price && (
                <Text
                  as="span"
                  className="font-bold  max-2xs:text-sm text-base"
                >
                  ${(
              item?.price *
              (cartList?.cartProducts?.products?.find(
                (product: any) => product?.item_id == item.objectID
              )?.quantity || 1)
            ).toFixed(2)}{item?.uom === "KG"
              ? "/kg"
              : ""}
                </Text>
              )
            )}
          </Text>
        </Box>
        {/* <Box as="div" className="w-1/4 flex justify-end pl-1">
          <Text
            as="p"
            className="flex gap-1 items-center max-2xs:text-sm text-base"
          >
            {promotionsItemsData && promotionsItemsData?.length > 0 ? (
              <>
                {promotionsItemsData?.[0]?.offerIdentifier ===
                  "No Min Condition" &&
                  promotionsItemsData?.[0]?.salePrice <
                    promotionsItemsData?.[0]?.price && (
                    <Text as="span" className="text-red200 font-bold">
                      ${(promotionsItemsData?.[0]?.salePrice *
              (cartList?.cartProducts?.products?.find(
                (product: any) => product?.item_id == item.objectID
              )?.quantity || 1))?.toFixed(2)}
                    </Text>
                  )}
                <Text
                  as="span"
                  className={`font-bold max-2xs:text-sm text-base ${
                    promotionsItemsData?.[0]?.offerIdentifier ===
                      "No Min Condition" &&
                    promotionsItemsData?.[0]?.salePrice <
                      promotionsItemsData?.[0]?.price
                      ? "line-through"
                      : ""
                  }`}
                >
                  ${(promotionsItemsData?.[0]?.price *
              (cartList?.cartProducts?.products?.find(
                (product: any) => product?.item_id == item.objectID
              )?.quantity || 1))?.toFixed(2)}
                </Text>
              </>
            ) : (
              item?.price && (
                <Text
                  as="span"
                  className="font-bold  max-2xs:text-sm text-base"
                >
                  ${(
              item?.price *
              (cartList?.cartProducts?.products?.find(
                (product: any) => product?.item_id == item.objectID
              )?.quantity || 1)
            ).toFixed(2)}
                </Text>
              )
            )}
          </Text>
        </Box> */}
      </Box>
      <Box as="div" className="flex items-center justify-end pb-3">
        <Box as="div" className="w-1/3 flex justify-end">
          <AnimatedQuantityButton
            isFullView={true}
            productQuantity={
              cartList?.cartProducts?.products?.find(
                (product: any) => product?.item_id == item.objectID
              )?.quantity || 1
            }
            clickCallbackHandler={(newQuantity) => {
              setClickCount((prev) => prev + 1);

              setArticleId(item?.objectID);
              const oldQuantity =
                cartList?.cartProducts?.products?.find(
                  (product: any) => product?.item_id == item.objectID
                )?.quantity || 1;
              if (newQuantity > oldQuantity) {
                setdecrementDisable(true);
              } else {
                setincrementDisable(true);
              }

              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }
              timerRef.current = setTimeout(() => {
                if (clickCount == 0 || clickCount == 1 || !userLoggedIn) {
                  addToCart(newQuantity == 0 ? -1 : newQuantity);
                } else {
                  setProductQty(newQuantity == 0 ? -1 : newQuantity);
                }
                setClickCount(0);
              }, 200);
              // setProductQty(newQuantity == 0 ? -1 : newQuantity);
              setIsProductQtyClick(true);
            }}
            isIncrementDisabled={incrementDisable}
            isDecrementDisabled={decrementDisable}
          />
        </Box>
      </Box>
    </Box>
  );
};
